import React, { useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import SecurityIcon from "@material-ui/icons/Security";
import MaterialTable, { Column } from "material-table";
import BreadcrumbNav from "../../../../components/BreadcrumbNav/BreadcrumbNav";
import CustomButton from "../../../../components/Button/CustomButton";
import DeleteConfirmationDialog from "../../../../components/DeleteConfirmationDialog/DeleteConfirmationDialog";
import { materialTableES } from "../../../../services/materialTableES";
import { errorToast, successToast } from "../../../../services/toasts";
import {
  useCreateRbacMember,
  useDeleteRbacMember,
  useGetRbacMembers,
  useGetRbacRoles,
  useSetRbacMemberRoles,
  useToggleRbacMemberActive,
} from "../../../../utils/react-query/queryRbac/queries";
import {
  SupplierEmployeeFormValues,
  SupplierRbacMember,
  SupplierRbacRole,
} from "../../types";
import styles from "./EmployeesManagement.module.scss";

const INITIAL_FORM: SupplierEmployeeFormValues = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  cedula: "",
  password: "",
  role_ids: [],
};

const getErrorMessage = (error: unknown, fallbackMessage: string) => {
  return error instanceof Error ? error.message : fallbackMessage;
};

const EmployeesManagement = () => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isRolesDialogOpen, setIsRolesDialogOpen] = useState(false);
  const [employeeForm, setEmployeeForm] =
    useState<SupplierEmployeeFormValues>(INITIAL_FORM);
  const [selectedMember, setSelectedMember] =
    useState<SupplierRbacMember | null>(null);
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([]);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
    onConfirm: () => {},
  });

  const { data: members = [], isLoading: isLoadingMembers } =
    useGetRbacMembers();
  const {
    data: roles = [],
    isLoading: isLoadingRoles,
    isError: hasRolesError,
    error: rolesError,
  } = useGetRbacRoles();
  const { mutateAsync: createMember, isLoading: isCreatingMember } =
    useCreateRbacMember();
  const { mutateAsync: toggleMemberActive } = useToggleRbacMemberActive();
  const { mutateAsync: deleteMember } = useDeleteRbacMember();
  const { mutateAsync: setMemberRoles, isLoading: isSavingRoles } =
    useSetRbacMemberRoles();

  const summary = useMemo(() => {
    const activeMembers = members.filter((member) => member.is_active).length;

    return {
      totalMembers: members.length,
      activeMembers,
      rolesAvailable: roles.length,
    };
  }, [members, roles]);

  const rolesUnavailable = !isLoadingRoles && roles.length === 0;
  const canSubmitEmployee =
    !isLoadingRoles && !hasRolesError && roles.length > 0;
  const canSubmitRolesAssignment =
    !isSavingRoles &&
    !isLoadingRoles &&
    !hasRolesError &&
    roles.length > 0 &&
    !!selectedMember;

  const handleInputChange =
    (field: keyof Omit<SupplierEmployeeFormValues, "role_ids">) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setEmployeeForm((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
    };

  const toggleRoleInCreateForm = (roleId: number) => {
    setEmployeeForm((prev) => ({
      ...prev,
      role_ids: prev.role_ids.includes(roleId)
        ? prev.role_ids.filter((currentRoleId) => currentRoleId !== roleId)
        : [...prev.role_ids, roleId],
    }));
  };

  const toggleRoleInAssignment = (roleId: number) => {
    setSelectedRoleIds((prev) =>
      prev.includes(roleId)
        ? prev.filter((currentRoleId) => currentRoleId !== roleId)
        : [...prev, roleId]
    );
  };

  const closeCreateDialog = () => {
    setEmployeeForm(INITIAL_FORM);
    setIsCreateDialogOpen(false);
  };

  const handleCreateEmployee = async () => {
    if (!canSubmitEmployee) {
      return errorToast(
        hasRolesError
          ? getErrorMessage(rolesError, "No fue posible cargar los roles")
          : "No hay roles disponibles para asignar"
      );
    }

    const requiredFields = [
      employeeForm.first_name,
      employeeForm.last_name,
      employeeForm.email,
      employeeForm.phone,
      employeeForm.password,
    ];

    if (requiredFields.some((field) => !field.trim())) {
      return errorToast("Completa todos los campos obligatorios del empleado");
    }

    if (employeeForm.role_ids.length === 0) {
      return errorToast("Selecciona al menos un rol para el empleado");
    }

    try {
      await createMember({
        ...employeeForm,
        cedula: employeeForm.cedula.trim() || undefined,
      });

      successToast("Empleado creado con éxito");
      closeCreateDialog();
    } catch (error) {
      errorToast(getErrorMessage(error, "No fue posible crear el empleado"));
    }
  };

  const handleToggleActive = async (member: SupplierRbacMember) => {
    try {
      await toggleMemberActive({
        memberId: member.id,
        is_active: !member.is_active,
      });

      successToast(
        `El empleado fue ${
          member.is_active ? "desactivado" : "activado"
        } con éxito`
      );
    } catch (error) {
      errorToast(
        getErrorMessage(
          error,
          "No fue posible actualizar el estado del empleado"
        )
      );
    }
  };

  const openRolesDialog = (member: SupplierRbacMember) => {
    setSelectedMember(member);
    setSelectedRoleIds(member.roles.map((role) => role.id));
    setIsRolesDialogOpen(true);
  };

  const closeRolesDialog = () => {
    setSelectedMember(null);
    setSelectedRoleIds([]);
    setIsRolesDialogOpen(false);
  };

  const handleSaveAssignedRoles = async () => {
    if (!isLoadingRoles && (hasRolesError || rolesUnavailable)) {
      return errorToast(
        hasRolesError
          ? getErrorMessage(rolesError, "No fue posible cargar los roles")
          : "No hay roles disponibles para asignar"
      );
    }

    if (!selectedMember) {
      return;
    }

    try {
      await setMemberRoles({
        memberId: selectedMember.id,
        role_ids: selectedRoleIds,
      });

      successToast("Roles del empleado actualizados con éxito");
      closeRolesDialog();
    } catch (error) {
      errorToast(
        getErrorMessage(
          error,
          "No fue posible actualizar los roles del empleado"
        )
      );
    }
  };

  const handleDeleteMember = async (memberId: number) => {
    try {
      await deleteMember(memberId);
      successToast("Empleado eliminado con éxito");
    } catch (error) {
      errorToast(getErrorMessage(error, "No fue posible eliminar el empleado"));
    }
  };

  const renderRolesSelector = (
    selectedIds: number[],
    onToggleRole: (roleId: number) => void
  ) => {
    if (isLoadingRoles) {
      return (
        <Paper className={styles.emptyState} elevation={0}>
          <Typography className={styles.primaryText}>
            Cargando roles desde el backend...
          </Typography>
          <Typography className={styles.secondaryText}>
            Espera a que se complete la consulta para asignar roles.
          </Typography>
        </Paper>
      );
    }

    if (hasRolesError) {
      return (
        <Paper className={styles.emptyState} elevation={0}>
          <Typography className={styles.primaryText}>
            No fue posible cargar los roles
          </Typography>
          <Typography className={styles.secondaryText}>
            {getErrorMessage(
              rolesError,
              "Verifica la respuesta de /v2/rbac/roles e inténtalo nuevamente."
            )}
          </Typography>
        </Paper>
      );
    }

    if (rolesUnavailable) {
      return (
        <Paper className={styles.emptyState} elevation={0}>
          <Typography className={styles.primaryText}>
            Aún no hay roles disponibles
          </Typography>
          <Typography className={styles.secondaryText}>
            Crea al menos un rol en la sección de Roles y permisos antes de
            registrar empleados internos.
          </Typography>
        </Paper>
      );
    }

    return (
      <Grid container spacing={2} className={styles.rolesGrid}>
        {roles.map((role) => (
          <Grid item xs={12} sm={6} key={role.id}>
            <Paper className={styles.roleSelector} elevation={0}>
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={selectedIds.includes(role.id)}
                    onChange={() => onToggleRole(role.id)}
                  />
                }
                label={
                  <div>
                    <Typography className={styles.primaryText}>
                      {role.name}
                    </Typography>
                    <Typography className={styles.secondaryText}>
                      {role.description || "Sin descripción"}
                    </Typography>
                  </div>
                }
              />
            </Paper>
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderRoleChips = (memberRoles: SupplierRbacRole[]) => {
    if (!memberRoles.length) {
      return (
        <Typography className={styles.secondaryText}>Sin roles</Typography>
      );
    }

    return (
      <div className={styles.rolesContainer}>
        {memberRoles.map((role) => (
          <span
            key={role.id}
            className={`${styles.roleChip} ${
              role.is_system ? styles.systemRoleChip : ""
            }`}
          >
            <span
              className={`${styles.roleChipText} ${
                role.is_system ? styles.systemRoleChipText : ""
              }`}
            >
              {role.name}
            </span>
          </span>
        ))}
      </div>
    );
  };

  const columns: Column<SupplierRbacMember>[] = [
    {
      title: "Empleado",
      field: "user.first_name",
      render: (rowData) => (
        <div>
          <Typography className={styles.primaryText}>
            {`${rowData.user?.first_name || ""} ${
              rowData.user?.last_name || ""
            }`}
          </Typography>
          <Typography className={styles.secondaryText}>
            {rowData.user?.cedula || "Sin cédula"}
          </Typography>
        </div>
      ),
    },
    {
      title: "Correo",
      field: "user.email",
      render: (rowData) => rowData.user?.email || "-",
    },
    {
      title: "Teléfono",
      field: "user.phone",
      render: (rowData) => rowData.user?.phone || "-",
    },
    {
      title: "Roles",
      field: "roles",
      sorting: false,
      render: (rowData) => renderRoleChips(rowData.roles || []),
    },
    {
      title: "Activo",
      field: "is_active",
      filtering: false,
      render: (rowData) => (
        <Switch
          color="primary"
          checked={rowData.is_active}
          onChange={() => handleToggleActive(rowData)}
        />
      ),
    },
    {
      title: "Acciones",
      field: "actions",
      sorting: false,
      filtering: false,
      render: (rowData) => (
        <div className={styles.rowActions}>
          <Tooltip title="Asignar roles">
            <IconButton onClick={() => openRolesDialog(rowData)}>
              <SecurityIcon className={styles.actionIconAccent} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar empleado">
            <IconButton
              onClick={() =>
                setConfirmDialog({
                  isOpen: true,
                  title: "Eliminar empleado",
                  subTitle:
                    "Esta acción retirará al empleado del proveedor. ¿Deseas continuar?",
                  onConfirm: async () => {
                    await handleDeleteMember(rowData.id);
                    setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
                  },
                })
              }
            >
              <DeleteIcon className={styles.actionIconDanger} />
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <section className={`layout-dashboard-container ${styles.page}`}>
      <div className="header-container">
        <Box className={styles.headerRow}>
          <div>
            <h4 className="header-title">Empleados</h4>
            <BreadcrumbNav
              firstText="Proveedor"
              secondText="Empleados"
              secondLink="/proveedor/empleados"
              thirdText="Gestión"
            />
          </div>
        </Box>
      </div>

      <DeleteConfirmationDialog
        confirmDialog={confirmDialog}
        setConfirmDialog={setConfirmDialog}
      />

      <Grid container spacing={2} className={styles.summaryGrid}>
        {[
          { label: "Total empleados", value: summary.totalMembers },
          { label: "Activos", value: summary.activeMembers },
          { label: "Roles disponibles", value: summary.rolesAvailable },
        ].map((item) => (
          <Grid item xs={12} sm={4} key={item.label}>
            <Paper className={styles.summaryCard} elevation={0}>
              <Typography className={styles.summaryLabel}>
                {item.label}
              </Typography>
              <Typography className={styles.summaryValue}>
                {item.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper className={styles.helperCard} elevation={0}>
        <Typography className={styles.primaryText}>
          Gestiona aquí los usuarios internos del proveedor.
        </Typography>
        <Typography className={styles.secondaryText}>
          Esta sección es independiente de los partners públicos registrados
          como asesores.
        </Typography>
      </Paper>

      <div className={styles.actionBar}>
        <CustomButton
          color="secondary"
          shape="little-rounded"
          icon={PersonAddIcon}
          onClick={() => setIsCreateDialogOpen(true)}
        >
          Crear empleado
        </CustomButton>
      </div>

      <MaterialTable
        title=""
        columns={columns}
        data={members}
        isLoading={isLoadingMembers || isLoadingRoles}
        localization={materialTableES}
        options={{
          search: true,
          filtering: false,
          actionsColumnIndex: -1,
        }}
      />

      <Dialog
        open={isCreateDialogOpen}
        onClose={closeCreateDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{ className: styles.dialogPaper }}
      >
        <DialogTitle>Crear empleado</DialogTitle>
        <DialogContent dividers className={styles.dialogContent}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nombre"
                variant="outlined"
                value={employeeForm.first_name}
                onChange={handleInputChange("first_name")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Apellido"
                variant="outlined"
                value={employeeForm.last_name}
                onChange={handleInputChange("last_name")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Correo"
                variant="outlined"
                value={employeeForm.email}
                onChange={handleInputChange("email")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Celular"
                variant="outlined"
                value={employeeForm.phone}
                onChange={handleInputChange("phone")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Cédula"
                variant="outlined"
                value={employeeForm.cedula}
                onChange={handleInputChange("cedula")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="password"
                label="Contraseña"
                variant="outlined"
                value={employeeForm.password}
                onChange={handleInputChange("password")}
              />
            </Grid>
          </Grid>

          <Box className={styles.dialogSectionHeader}>
            <Typography className={styles.primaryText}>
              Roles del empleado
            </Typography>
            <Typography className={styles.secondaryText}>
              Selecciona los roles que se asignarán al usuario interno.
            </Typography>
          </Box>

          {renderRolesSelector(employeeForm.role_ids, toggleRoleInCreateForm)}
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <CustomButton
            color="outlined"
            shape="little-rounded"
            onClick={closeCreateDialog}
          >
            Cancelar
          </CustomButton>
          <CustomButton
            color="secondary"
            shape="little-rounded"
            onClick={handleCreateEmployee}
            disabled={!canSubmitEmployee}
            isLoading={isCreatingMember}
          >
            Crear empleado
          </CustomButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isRolesDialogOpen}
        onClose={closeRolesDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{ className: styles.dialogPaper }}
      >
        <DialogTitle>
          {selectedMember
            ? `Roles de ${selectedMember.user?.first_name || "empleado"}`
            : "Roles del empleado"}
        </DialogTitle>
        <DialogContent dividers className={styles.dialogContent}>
          {renderRolesSelector(selectedRoleIds, toggleRoleInAssignment)}
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <CustomButton
            color="outlined"
            shape="little-rounded"
            onClick={closeRolesDialog}
          >
            Cancelar
          </CustomButton>
          <CustomButton
            color="secondary"
            shape="little-rounded"
            onClick={handleSaveAssignedRoles}
            disabled={!canSubmitRolesAssignment}
            isLoading={isSavingRoles}
          >
            Guardar roles
          </CustomButton>
        </DialogActions>
      </Dialog>
    </section>
  );
};

export default EmployeesManagement;
